import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent, type PointerEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ColumnDef,
  type ColumnLayout,
  columnLayoutCacheKey,
  columnWidth,
  defaultColumnLayout,
  dropSide,
  freezeColumnWidths,
  hasFixedWidths,
  isDefaultColumnLayout,
  moveColumn,
  moveColumnBy,
  normalizeColumnLayout,
  orderedColumns,
  readCachedColumnLayout,
  setColumnWidth,
  storedColumnLayout,
  toggleColumn,
  visibleColumns,
  writeCachedColumnLayout,
} from "@/lib/tableColumns";
import { getTableLayout, saveTableLayout, tableLayoutKeys, type StoredTableLayout } from "@/lib/tableLayouts.api";
import { fittedColumnWidth, headerWidths } from "./fitColumn";

// Its own drag type, so a header held over a drop zone elsewhere (a mail
// composer, an attachment field) is never taken for something to drop there.
const COLUMN_DRAG_TYPE = "application/x-wlx-column";

// A burst of changes — ticking three columns off — goes to the account as one.
const SAVE_DELAY_MS = 500;

// Everything a list table needs for its own column layout: which columns to
// draw, and the handlers for dragging a header to another place and dragging
// its edge to another width. The rules live in lib/tableColumns.ts; this is
// only the wiring. `defs` must be a stable (module-level) array; `table` is the
// list's key on the API (/table-layouts/{table}).
//
// The layout lives on the account, so it follows the person to another PC. The
// list opens in the copy this browser has — no flash of the default on every
// visit — and takes the account's layout when it arrives, unless something was
// already changed here in the meantime: then that change wins and is saved.
/** `fixed`: the columns start at their standard widths, not at the width of their content (21-09-2026). */
export interface ColumnLayoutOptions {
  fixed?: boolean;
}

export function useColumnLayout<T extends ColumnDef>(table: string, defs: readonly T[], userId: string, options: ColumnLayoutOptions = {}) {
  const queryClient = useQueryClient();
  const cacheKey = columnLayoutCacheKey(userId, table);
  const queryKey = useMemo(() => tableLayoutKeys.one(userId, table), [userId, table]);
  const [layout, setLayout] = useState<ColumnLayout>(() => readCachedColumnLayout(cacheKey, defs));
  // The current layout for handlers that outlive a render: a resize reads it on
  // every pointer move, long after the render that started it.
  const layoutRef = useRef(layout);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [resizing, setResizing] = useState<string | null>(null);

  const show = useCallback((next: ColumnLayout) => {
    layoutRef.current = next;
    setLayout(next);
  }, []);

  // Fetched once a session. Every change below also goes into this cache, so
  // coming back to the list later starts from the latest layout and not from
  // what the account held when the session began.
  const stored = useQuery({
    queryKey,
    queryFn: () => getTableLayout(table),
    staleTime: Infinity,
    retry: false,
  });

  const changedHere = useRef(false);
  const adopted = useRef(false);
  useEffect(() => {
    if (adopted.current || !stored.data) return;
    adopted.current = true;
    if (changedHere.current) return;
    const fromAccount = normalizeColumnLayout(stored.data.layout, defs);
    writeCachedColumnLayout(cacheKey, fromAccount, defs);
    if (JSON.stringify(fromAccount) !== JSON.stringify(layoutRef.current)) show(fromAccount);
  }, [stored.data, cacheKey, defs, show]);

  // Saves go out one after another, so an older layout can never land on the
  // account after a newer one. Best effort: a failed save leaves the layout in
  // this browser, and the next change sends the whole layout again.
  const saving = useRef<Promise<unknown>>(Promise.resolve());
  const pending = useRef<{ body: ColumnLayout | null } | null>(null);
  const timer = useRef<number | null>(null);

  const flush = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    saving.current = saving.current
      .then(() => saveTableLayout(table, next.body))
      .catch(() => {
        /* see above: kept here, sent again with the next change */
      });
  }, [table]);

  // Leaving the list must not drop a change still waiting for its delay.
  useEffect(() => flush, [flush]);

  const persist = useCallback(
    (next: ColumnLayout) => {
      changedHere.current = true;
      writeCachedColumnLayout(cacheKey, next, defs);
      const body = storedColumnLayout(next, defs);
      // A first fetch still on its way would otherwise overwrite this with the
      // older layout it went to get.
      void queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<StoredTableLayout>(queryKey, { table, layout: body, updatedAt: null });
      pending.current = { body };
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(flush, SAVE_DELAY_MS);
    },
    [cacheKey, defs, flush, queryClient, queryKey, table],
  );

  const commit = useCallback(
    (next: ColumnLayout) => {
      if (next === layoutRef.current) return;
      show(next);
      persist(next);
    },
    [show, persist],
  );

  const endDrag = () => {
    setDragging(null);
    setDropTarget(null);
  };

  // On the header's sort button: a click still sorts, a drag moves the column.
  function dragSourceProps(id: string) {
    return {
      draggable: true,
      onDragStart: (e: DragEvent<HTMLElement>) => {
        e.dataTransfer.setData(COLUMN_DRAG_TYPE, id);
        e.dataTransfer.effectAllowed = "move";
        setDragging(id);
      },
      onDragEnd: endDrag,
    };
  }

  // On the header cell: where a dragged column can be dropped.
  function dropTargetProps(id: string) {
    return {
      onDragOver: (e: DragEvent<HTMLElement>) => {
        if (!dragging || dragging === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dropTarget !== id) setDropTarget(id);
      },
      onDragLeave: (e: DragEvent<HTMLElement>) => {
        // Moving onto the button inside the cell is not leaving the cell.
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
        setDropTarget((v) => (v === id ? null : v));
      },
      onDrop: (e: DragEvent<HTMLElement>) => {
        if (!dragging) return;
        e.preventDefault();
        commit(moveColumn(layoutRef.current, dragging, id));
        endDrag();
      },
    };
  }

  function headerClassName(id: string): string {
    const side = dragging && dropTarget === id ? dropSide(layout, dragging, id) : null;
    return [dragging === id ? "is-dragging" : "", side ? `is-drop-${side}` : ""].filter(Boolean).join(" ");
  }

  function startResize(e: PointerEvent<HTMLElement>, id: string) {
    if (e.button !== 0) return;
    // No text selection, and no drag of the header the edge sits in.
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget;
    // The width every column is drawn at right now, measured before anything
    // changes. Only used once the pointer actually moves: a click, or the two
    // clicks of a double-click, must leave the layout as it was.
    const measured = headerWidths(handle.closest("tr"));
    const startX = e.clientX;
    const startWidth = measured[id] ?? 0;
    const before = layoutRef.current;
    let frozen: ColumnLayout | null = null;

    const onMove = (ev: globalThis.PointerEvent) => {
      const dx = ev.clientX - startX;
      if (!frozen) {
        if (Math.abs(dx) < 1) return;
        frozen = freezeColumnWidths(layoutRef.current, measured);
      }
      // Columns before this one keep their width, so its left edge stays put
      // and the edge being dragged stays under the pointer.
      show(setColumnWidth(frozen, id, startWidth + dx));
    };
    const onEnd = () => {
      handle.removeEventListener("pointermove", onMove);
      setResizing(null);
      // Saved once, when the edge is let go — not on every pointer move.
      if (layoutRef.current !== before) persist(layoutRef.current);
    };
    handle.setPointerCapture(e.pointerId);
    handle.addEventListener("pointermove", onMove);
    // Fires for a normal release, a cancelled pointer and a lost capture alike.
    handle.addEventListener("lostpointercapture", onEnd, { once: true });
    setResizing(id);
  }

  // On the resize edge of a header cell.
  function resizeHandleProps(id: string) {
    return {
      className: `wl-th-resize${resizing === id ? " is-active" : ""}`,
      onPointerDown: (e: PointerEvent<HTMLElement>) => startResize(e, id),
      // As wide as the widest cell on screen, the header included (fitColumn.ts). The
      // other columns keep the width they are drawn at, so nothing else moves.
      onDoubleClick: (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        const th = e.currentTarget.closest("th");
        const width = th ? fittedColumnWidth(th) : null;
        if (!th || width === null) return;
        commit(setColumnWidth(freezeColumnWidths(layoutRef.current, headerWidths(th.parentElement)), id, width));
      },
      onClick: (e: MouseEvent<HTMLElement>) => e.stopPropagation(),
    };
  }

  const visible = visibleColumns(layout, defs);
  // A list with many columns opens at the widths chosen for it, so it fits the screen
  // instead of running as wide as its longest cell (Transportmiddelen, Chauffeurs).
  const fixed = options.fixed === true || hasFixedWidths(layout);

  return {
    layout,
    // The columns to draw, in order.
    visible,
    // All columns in order, hidden ones included — for the column menu.
    ordered: orderedColumns(layout, defs),
    // Whether the widths are fixed (see lib/tableColumns.ts). The table then
    // needs `wl-table--fixed`, a <colgroup> with widthOf, a filler column and
    // `tableWidth` as its minimum width.
    fixed,
    widthOf: (def: T) => columnWidth(layout, def),
    tableWidth: fixed ? visible.reduce((sum, def) => sum + columnWidth(layout, def), 0) : 0,
    resizing,
    isDefault: isDefaultColumnLayout(layout, defs),
    toggle: (id: string) => commit(toggleColumn(layoutRef.current, id, defs)),
    move: (dragged: string, target: string) => commit(moveColumn(layoutRef.current, dragged, target)),
    moveBy: (id: string, step: -1 | 1) => commit(moveColumnBy(layoutRef.current, id, step)),
    reset: () => commit(defaultColumnLayout(defs)),
    dragSourceProps,
    dropTargetProps,
    headerClassName,
    resizeHandleProps,
  };
}
